import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { UserService } from '../user/user.service';

/**
 * Single source of truth for "may the current user use the test UI?"
 * (route `/dev-tools` and the Test-Mode panel with its actions).
 *
 * Allowed for super admins and for sessions a super admin switched to via the
 * test UI (so they can keep switching between test users, #1047), and
 * unconditionally in the local development environment
 * (`!environment.production`; staging/prod are built with
 * `environment.prod.ts`) so that local E2E tests work without a super admin
 * session. The Test-Mode bar itself stays visible for everyone (#439) and is
 * deliberately NOT governed by this check.
 *
 * Client-side only: the TestData endpoints must be secured on the server.
 *
 * Deliberately separate from TestModeService: that one is injected by the HTTP
 * interceptor, and UserService depends on HttpClient, so merging them would
 * create a circular dependency.
 */
@Injectable({
  providedIn: 'root',
})
export class TestUiAccessService {
  private readonly userService = inject(UserService);

  public isLocalDevEnvironment(): boolean {
    return !environment.production;
  }

  /**
   * Access that does not depend on the super admin flag: the local dev
   * environment, or a session a super admin switched to via the test UI.
   * `TestUiGuardService` lets these through without the super admin
   * login/redirect handling.
   */
  public bypassesSuperAdminCheck(): boolean {
    return (
      this.isLocalDevEnvironment() || this.userService.isSwitchedFromSuperAdmin()
    );
  }

  public canUseTestUi(): boolean {
    return this.bypassesSuperAdminCheck() || this.userService.isSuperAdmin();
  }
}
